/**
 * The left tree pane's width and collapsed state, remembered per browser
 * (#403, the owner's own layout feedback on §7.8).
 *
 * Wrapped in try/catch the same way `components/list/layout.ts` wraps its
 * own `localStorage` calls: a preference is not worth a tree that fails to
 * render because this browser has site data blocked.
 */

export const MIN_TREE_WIDTH = 200;
export const MAX_TREE_WIDTH = 480;
export const DEFAULT_TREE_WIDTH = 256;

const WIDTH_KEY = "estelle.docs.treeWidth";
const COLLAPSED_KEY = "estelle.docs.treeCollapsed";

/** `width`, forced into the 200–480 range the drag handle itself enforces. */
export function clampTreeWidth(width: number): number {
  return Math.min(MAX_TREE_WIDTH, Math.max(MIN_TREE_WIDTH, width));
}

export function readTreeWidth(): number {
  try {
    const stored = Number(localStorage.getItem(WIDTH_KEY));
    return Number.isFinite(stored) && stored > 0
      ? clampTreeWidth(stored)
      : DEFAULT_TREE_WIDTH;
  } catch {
    return DEFAULT_TREE_WIDTH;
  }
}

export function writeTreeWidth(width: number): void {
  try {
    localStorage.setItem(WIDTH_KEY, String(clampTreeWidth(width)));
  } catch {
    // Unwritable storage costs the preference, not the resize.
  }
}

export function readTreeCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

export function writeTreeCollapsed(collapsed: boolean): void {
  try {
    if (collapsed) localStorage.setItem(COLLAPSED_KEY, "1");
    else localStorage.removeItem(COLLAPSED_KEY);
  } catch {
    // Unwritable storage costs the preference, not the collapse.
  }
}
